import { ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { useStoreMeta } from '@/stores/meta'
import { facilityHeaderCardKeys } from '@/modules/axios/components/dashboard/dashboardConfig'

import type { TitledElementFilter } from '@/shared/types/filters.type'

type Timezone = string
type Currency = string
type SetTimezonePayload = { timezone: Timezone }
type SetBreadcrumbIconsPayload = { breadcrumbIcons: boolean }

const LOCAL_STORAGE_KEY_CURRENCY = 'turing_currency'
const DEFAULT_CURRENCY: Currency = 'NOK'

const readStoredCurrency = (): Currency => {
  try {
    return localStorage.getItem(LOCAL_STORAGE_KEY_CURRENCY) ?? DEFAULT_CURRENCY
  } catch {
    return DEFAULT_CURRENCY
  }
}

const LOCAL_STORAGE_KEY_AXIOS_ROADMAP_TOOLTIP_PARAMETERS = 'turing_axios_roadmap_tooltip_parameters'
const DEFAULT_AXIOS_ROADMAP_TOOLTIP_PARAMETERS = [
  'fishNumber',
  'fishAvgWeight',
  'biomass',
  'fishDensity',
  'fishDensityPeak',
  'co2Concentration',
  'co2ConcentrationPeak'
]

const LOCAL_STORAGE_KEY_AXIOS_ROADMAP_HEADER_PARAMETERS = 'turing_axios_roadmap_header_parameters'

const readStoredStringArray = (storageKey: string, fallback: readonly string[]): string[] => {
  try {
    const stored = localStorage.getItem(storageKey)
    if (!stored) return [...fallback]

    const parsed = JSON.parse(stored)
    if (!Array.isArray(parsed) || !parsed.every((item) => typeof item === 'string')) {
      return [...fallback]
    }

    return parsed
  } catch {
    return [...fallback]
  }
}

const persistStringArray = (storageKey: string, value: string[]) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(value))
  } catch (error) {
    console.warn(`Failed to persist ${storageKey} to localStorage:`, error)
  }
}

type UserSettings = {
  sidebar_collapsed: boolean
  breadcrumb_icons: boolean
  currency: Currency
  number_decimals_max: number
  timezone: Timezone
  axios_roadmap_tooltip_parameters: string[]
  axios_roadmap_header_parameters: string[]

  routes: {
    projects: {
      filter: TitledElementFilter
    }
    project: {
      filter: TitledElementFilter
    }
  }
}

export const useStoreUser = defineStore('user', () => {
  const storeMeta = useStoreMeta()

  // State settings
  const stateSettings = ref<UserSettings>({
    sidebar_collapsed: true,
    breadcrumb_icons: true,
    timezone: 'Europe/Oslo',
    currency: readStoredCurrency(),
    number_decimals_max: 2,
    axios_roadmap_tooltip_parameters: readStoredStringArray(
      LOCAL_STORAGE_KEY_AXIOS_ROADMAP_TOOLTIP_PARAMETERS,
      DEFAULT_AXIOS_ROADMAP_TOOLTIP_PARAMETERS
    ),
    axios_roadmap_header_parameters: readStoredStringArray(
      LOCAL_STORAGE_KEY_AXIOS_ROADMAP_HEADER_PARAMETERS,
      facilityHeaderCardKeys
    ),
    routes: {
      projects: {
        filter: {
          key: 'updated',
          asc: true
        }
      },
      project: {
        filter: {
          key: 'updated',
          asc: true
        }
      }
    }
  })

  watch(
    () => stateSettings.value.currency,
    (currency) => {
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY_CURRENCY, currency)
      } catch (error) {
        console.warn('Failed to persist currency to localStorage:', error)
      }
    }
  )

  watch(
    () => stateSettings.value.axios_roadmap_tooltip_parameters,
    (parameters) => persistStringArray(LOCAL_STORAGE_KEY_AXIOS_ROADMAP_TOOLTIP_PARAMETERS, parameters),
    { deep: true }
  )

  watch(
    () => stateSettings.value.axios_roadmap_header_parameters,
    (parameters) => persistStringArray(LOCAL_STORAGE_KEY_AXIOS_ROADMAP_HEADER_PARAMETERS, parameters),
    { deep: true }
  )

  // A stored currency missing from the loaded rates would silently zero out every converted figure.
  watch(
    () => storeMeta.currencyCodes,
    (codes) => {
      if (codes.length === 0) return
      if (!codes.includes(stateSettings.value.currency)) {
        stateSettings.value.currency = codes.includes(DEFAULT_CURRENCY) ? DEFAULT_CURRENCY : codes[0]
      }
    }
  )

  // State settings actions
  const setTimezone = (payload: SetTimezonePayload): void => {
    stateSettings.value.timezone = payload.timezone
  }

  const toggleSidebar = (): void => {
    stateSettings.value.sidebar_collapsed = !stateSettings.value.sidebar_collapsed
  }

  const setBreadcrumbIcons = (payload: SetBreadcrumbIconsPayload): void => {
    stateSettings.value.breadcrumb_icons = payload.breadcrumbIcons
  }

  const toggleBreadcrumbIcons = (): void => {
    stateSettings.value.breadcrumb_icons = !stateSettings.value.breadcrumb_icons
  }

  return {
    // State settings
    stateSettings,
    setTimezone,
    toggleSidebar,
    setBreadcrumbIcons,
    toggleBreadcrumbIcons
  }
})
